import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import type { Locale } from '../api/types'
import { formatDate, formatMoney, formatNumber, formatUsd, pluralCategory } from '../lib/format'
import { en } from './en'
import { sq, type MessageKey } from './sq'

export type { MessageKey }

/** Base names of messages that have `.one` and `.other` forms. */
export type PluralKey = MessageKey extends infer K ? (K extends `${infer B}.one` ? B : never) : never

const DICTIONARIES: Record<Locale, Record<string, string>> = { sq, en }
const GLOBAL_KEY = 'fjalim.locale'

type Vars = Record<string, string | number>

export function translate(locale: Locale, key: MessageKey, vars?: Vars): string {
  const template = DICTIONARIES[locale][key] ?? DICTIONARIES.sq[key] ?? key
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? `{${name}}`))
}

export function isLocale(value: unknown): value is Locale {
  return value === 'sq' || value === 'en'
}

function storageKey(userId: string | null): string {
  return userId ? `${GLOBAL_KEY}.${userId}` : GLOBAL_KEY
}

/** Saved per user; falls back to the browser-wide choice, then the account setting, then Albanian. */
export function readLocale(userId: string | null, accountLocale?: Locale): Locale {
  try {
    const own = localStorage.getItem(storageKey(userId))
    if (isLocale(own)) return own
    const global = localStorage.getItem(GLOBAL_KEY)
    if (isLocale(global)) return global
  } catch {
    // Storage can be unavailable (private mode); fall through.
  }
  return accountLocale ?? 'sq'
}

export interface I18n {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: MessageKey, vars?: Vars) => string
  /** Counted message: picks `<key>.one` or `<key>.other` for `n` (also available as {n}). */
  tn: (key: PluralKey, n: number, vars?: Vars) => string
  /** Message for an API error code, falling back to the server text. */
  errorText: (error: unknown) => string
  eur: (microEur: number) => string
  usd: (amount: number) => string
  number: (n: number) => string
  date: (iso: string, withTime?: boolean) => string
  longDate: (value?: Date) => string
}

const I18nContext = createContext<I18n | null>(null)

export function I18nProvider({
  userId,
  accountLocale,
  children,
}: {
  userId: string | null
  accountLocale?: Locale
  children: ReactNode
}) {
  const [locale, setLocaleState] = useState<Locale>(() => readLocale(userId, accountLocale))

  // The signed-in user changes (login/logout): load that user's saved choice.
  useEffect(() => {
    setLocaleState(readLocale(userId, accountLocale))
  }, [userId, accountLocale])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback(
    (next: Locale) => {
      setLocaleState(next)
      try {
        localStorage.setItem(storageKey(userId), next)
        localStorage.setItem(GLOBAL_KEY, next)
      } catch {
        // Not persisted; the choice still applies for this session.
      }
    },
    [userId],
  )

  const value = useMemo<I18n>(() => {
    const t = (key: MessageKey, vars?: Vars) => translate(locale, key, vars)
    return {
      locale,
      setLocale,
      t,
      tn: (key, n, vars) => t(`${key}.${pluralCategory(locale, n)}` as MessageKey, { n, ...vars }),
      errorText: (error) => {
        if (error instanceof ApiError) {
          const key = `err.${error.code}` as MessageKey
          if (key in sq) return t(key)
          return error.message || t('err.generic')
        }
        return t('err.generic')
      },
      eur: (micro) => formatMoney(micro, locale),
      usd: (amount) => formatUsd(amount, locale),
      number: (n) => formatNumber(n, locale),
      date: (iso, withTime = false) => formatDate(iso, locale, { time: withTime }),
      longDate: (value = new Date()) => formatDate(value, locale, { weekday: true }),
    }
  }, [locale, setLocale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside I18nProvider')
  return ctx
}
